import { COLORS } from "../shared/constants";
import { useState } from "react";

export default // ─── Assessment Trend Graphs ──────────────────────────────────
function AssessmentTrendGraphs({ assessments, assessmentScales, treatments }) {
  if (!assessments || assessments.length === 0) {
    return <div style={{ padding: "32px", textAlign: "center", color: COLORS.textSecondary, fontSize: "13px" }}>No assessment data to chart. Administer at least one assessment to see trends.</div>;
  }

  // Group assessments by scale
  const byScale = {};
  assessments.forEach(a => {
    if (!byScale[a.scale_id]) byScale[a.scale_id] = [];
    byScale[a.scale_id].push(a);
  });

  const scaleIds = Object.keys(byScale);

  return (
    <div style={{ display: "grid", gridTemplateColumns: scaleIds.length > 1 ? "1fr 1fr" : "1fr", gap: "16px" }}>
      {scaleIds.map(scaleId => {
        const scale = assessmentScales.find(s => s.id === scaleId);
        if (!scale) return null;
        const data = byScale[scaleId].sort((a, b) => new Date(a.administered_at) - new Date(b.administered_at));
        const bands = scale.severity_bands || [];
        const maxScore = scale.max_score || Math.max(...data.map(d => d.total_score), 10);
        const baseline = data.find(d => d.is_baseline);

        // Chart dimensions
        const W = 420, H = 220, PAD_L = 40, PAD_R = 20, PAD_T = 20, PAD_B = 50;
        const chartW = W - PAD_L - PAD_R;
        const chartH = H - PAD_T - PAD_B;

        const xScale = (i) => PAD_L + (data.length > 1 ? (i / (data.length - 1)) * chartW : chartW / 2);
        const yScale = (v) => PAD_T + chartH - ((v / maxScore) * chartH);

        // Build path
        const points = data.map((d, i) => `${xScale(i)},${yScale(d.total_score)}`);
        const linePath = points.length > 1 ? `M ${points.join(" L ")}` : "";

        const formatShortDate = (d) => {
          if (!d) return "";
          const dt = new Date(d);
          return `${dt.getMonth() + 1}/${dt.getDate()}`;
        };

        return (
          <div key={scaleId} style={{ background: COLORS.inputBg, borderRadius: "10px", padding: "16px", border: `1px solid ${COLORS.borderLight}` }}>
            <div style={{ fontSize: "13px", fontWeight: "600", color: COLORS.text, marginBottom: "8px" }}>{scale.short_name}</div>
            <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", height: "auto" }}>
              {/* Severity band backgrounds */}
              {bands.map((band, i) => {
                const y1 = yScale(Math.min(band.max_score, maxScore));
                const y2 = yScale(band.min_score);
                return <rect key={i} x={PAD_L} y={y1} width={chartW} height={Math.max(0, y2 - y1)} fill={band.color} opacity="0.12" />;
              })}

              {/* Remission zone */}
              {scale.remission_cutoff != null && (
                <>
                  <rect x={PAD_L} y={yScale(scale.remission_cutoff)} width={chartW} height={yScale(0) - yScale(scale.remission_cutoff)} fill="#22c55e" opacity="0.08" />
                  <line x1={PAD_L} y1={yScale(scale.remission_cutoff)} x2={PAD_L + chartW} y2={yScale(scale.remission_cutoff)} stroke="#22c55e" strokeDasharray="4 4" strokeWidth="1" opacity="0.5" />
                  <text x={PAD_L + 4} y={yScale(scale.remission_cutoff) - 3} fontSize="8" fill="#22c55e" opacity="0.7">Remission ≤{scale.remission_cutoff}</text>
                </>
              )}

              {/* Baseline dashed line */}
              {baseline && (
                <line x1={PAD_L} y1={yScale(baseline.total_score)} x2={PAD_L + chartW} y2={yScale(baseline.total_score)} stroke={COLORS.textSecondary} strokeDasharray="6 3" strokeWidth="1" opacity="0.4" />
              )}

              {/* Response threshold line (50% of baseline) */}
              {baseline && baseline.total_score > 0 && (
                <>
                  <line x1={PAD_L} y1={yScale(baseline.total_score * 0.5)} x2={PAD_L + chartW} y2={yScale(baseline.total_score * 0.5)} stroke={COLORS.accent} strokeDasharray="3 3" strokeWidth="1" opacity="0.4" />
                  <text x={PAD_L + chartW - 4} y={yScale(baseline.total_score * 0.5) - 3} fontSize="8" fill={COLORS.accent} opacity="0.6" textAnchor="end">50% Response</text>
                </>
              )}

              {/* Y axis labels */}
              {[0, Math.round(maxScore * 0.25), Math.round(maxScore * 0.5), Math.round(maxScore * 0.75), maxScore].map((v, i) => (
                <text key={i} x={PAD_L - 6} y={yScale(v) + 3} fontSize="9" fill={COLORS.textSecondary} textAnchor="end">{v}</text>
              ))}

              {/* Line */}
              {linePath && <path d={linePath} fill="none" stroke={COLORS.primary} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />}

              {/* Data points + labels */}
              {data.map((d, i) => (
                <g key={i}>
                  <circle cx={xScale(i)} cy={yScale(d.total_score)} r="5" fill={d.meets_remission ? "#22c55e" : d.meets_response ? COLORS.accent : COLORS.primary} stroke="#FFF" strokeWidth="2" />
                  <text x={xScale(i)} y={yScale(d.total_score) - 10} fontSize="10" fontWeight="700" fill={COLORS.text} textAnchor="middle">{d.total_score}</text>
                  {/* X axis date */}
                  <text x={xScale(i)} y={H - PAD_B + 16} fontSize="8" fill={COLORS.textSecondary} textAnchor="middle">{formatShortDate(d.administered_at)}</text>
                  {/* Session number */}
                  {d.session_number_at_admin != null && (
                    <text x={xScale(i)} y={H - PAD_B + 28} fontSize="7" fill={COLORS.textSecondary} textAnchor="middle">S{d.session_number_at_admin}</text>
                  )}
                </g>
              ))}

              {/* Severity band labels on right */}
              {bands.map((band, i) => {
                const midY = (yScale(band.min_score) + yScale(Math.min(band.max_score, maxScore))) / 2;
                return <text key={i} x={PAD_L + chartW + 4} y={midY + 3} fontSize="7" fill={band.color} opacity="0.8">{band.label}</text>;
              })}
            </svg>

            {/* Latest score summary */}
            {data.length > 0 && (() => {
              const latest = data[data.length - 1];
              return (
                <div style={{ display: "flex", gap: "12px", marginTop: "6px", fontSize: "11px", color: COLORS.textSecondary }}>
                  <span>Latest: <strong style={{ color: COLORS.text }}>{latest.total_score}</strong> ({latest.severity_label})</span>
                  {latest.percent_change_from_baseline != null && (
                    <span style={{ color: latest.percent_change_from_baseline <= -30 ? COLORS.accent : latest.percent_change_from_baseline >= 0 ? COLORS.danger : COLORS.warning }}>
                      {latest.percent_change_from_baseline > 0 ? "+" : ""}{latest.percent_change_from_baseline}% from baseline
                    </span>
                  )}
                  <span>{data.length} assessment{data.length !== 1 ? "s" : ""}</span>
                </div>
              );
            })()}
          </div>
        );
      })}
    </div>
  );
}


// ─── Patient Detail Screen ───────────────────────────────────

